import React from 'react';
import { Check, Clock, MinusCircle, X } from 'lucide-react';
import { leaveStepLabel } from '../utils/leaveApproval';

/**
 * Read-only view of a leave request's approval chain: each step, who is on it, and
 * where the request has got to.
 *
 * State is derived rather than stored. A step below current_step with no decision on
 * it was skipped, which happens when nobody could be resolved for it — a line
 * manager step for a requester who has none.
 */

const MODE_NOTE = {
  any: 'any one of them',
  all: 'all of them',
};

const stepState = (step, currentStep, requestStatus) => {
  const rejected = step.decisions.find((d) => d.decision === 'rejected');
  if (rejected) return { key: 'rejected', icon: X, tone: 'text-danger-600' };
  if (step.decisions.length) return { key: 'approved', icon: Check, tone: 'text-success-600' };
  if (step.position < currentStep) return { key: 'skipped', icon: MinusCircle, tone: 'text-gray-400' };
  if (step.position === currentStep && requestStatus === 'pending') {
    return { key: 'waiting', icon: Clock, tone: 'text-warning-600' };
  }
  return { key: 'upcoming', icon: Clock, tone: 'text-gray-300' };
};

const STATE_LABELS = {
  rejected: 'Rejected',
  approved: 'Approved',
  skipped: 'Skipped, nobody to ask',
  waiting: 'Waiting',
  upcoming: 'Not yet reached',
};

const LeaveApprovalTrail = ({ approval, requestStatus }) => {
  const steps = (approval && approval.steps) || [];
  if (!steps.length) return null;

  const currentStep = approval.current_step || 1;

  return (
    <div data-testid="leave-detail-chain" className="bg-white rounded-lg shadow-soft border border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-base font-semibold text-gray-900 flex items-center">
          <Check className="h-4 w-4 mr-2 text-primary-600" />
          Approval Chain
        </h2>
      </div>
      <div className="divide-y divide-gray-100">
        {steps.map((step) => {
          const state = stepState(step, currentStep, requestStatus);
          const Icon = state.icon;
          return (
            <div
              key={step.position}
              data-testid={`leave-detail-chain-step-${step.position}`}
              className="px-6 py-3 flex items-start gap-3 text-sm"
            >
              <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${state.tone}`} />
              <div className="flex-1">
                <p className="font-medium text-gray-900">
                  {step.position}. {leaveStepLabel(step)}
                  {step.mode === 'all' && (
                    <span className="ml-2 text-xs font-normal text-gray-500">({MODE_NOTE.all})</span>
                  )}
                </p>
                {step.decisions.length === 0 && (
                  <p className="text-gray-500 mt-0.5">{STATE_LABELS[state.key]}</p>
                )}
                {step.decisions.map((decision, index) => (
                  <p key={index} className="text-gray-600 mt-0.5">
                    {decision.decision === 'rejected' ? 'Rejected' : 'Approved'} by {decision.approver_name}
                    {decision.decided_at && ` on ${new Date(decision.decided_at).toLocaleDateString()}`}
                    {decision.comments && `: ${decision.comments}`}
                  </p>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LeaveApprovalTrail;
